/** Intro / outro thumbnail cards (image or video + title) prepended/appended on export. */

import { computeSubtitleLayerTextLayout } from '@/lib/subtitles/subtitleEditorLayout';
import {
  normalizeSubtitleLayer,
  subtitleLayerFromLegacyAnchors,
  subtitleLayerToExportAnchors,
} from '@/lib/subtitles/subtitleLayer';
import { subtitleStyleToExportPayload } from '@/lib/subtitles/subtitleStyleConfig';
import { normalizeVideoSubtitleStyle } from '@/lib/videoEditorProjectFile';
import { exportTargetSizeForAspect } from '@/lib/videoExportFraming';
import { mediaUrlForPlayback, persistedMediaUrl } from '@/lib/mediaUrls';

export const DEFAULT_VIDEO_BOOKEND_SLOT = {
  enabled: false,
  mediaType: 'image',
  url: '',
  title: '',
  durationSeconds: 4,
};

export const DEFAULT_VIDEO_BOOKENDS = {
  intro: { ...DEFAULT_VIDEO_BOOKEND_SLOT },
  outro: { ...DEFAULT_VIDEO_BOOKEND_SLOT },
};

/** Default centered title style for bookend cards. */
export function bookendDefaultTitleStyle(aspectRatio = '16:9') {
  return normalizeVideoSubtitleStyle({
    anchorX: 0.5,
    anchorY: 0.5,
    fontSize: 32,
    color: '#ffffff',
    outlineWidth: 2,
    outlineColor: '#000000',
    bold: true,
    layer: {
      x: 0.5,
      y: 0.5,
      width: 0.88,
      height: 0.22,
      rotation: 0,
      textAlign: 'center',
    },
  }, aspectRatio);
}

function persistBookendMediaUrl(url) {
  const raw = String(url || '').trim();
  if (!raw || raw.startsWith('blob:')) return '';
  return persistedMediaUrl(raw) || raw;
}

function normalizeSlot(raw, fallbackTitle = '', aspectRatio = '16:9') {
  const src = raw && typeof raw === 'object' ? raw : {};
  let url = persistBookendMediaUrl(src.url);
  let urlS3 = persistBookendMediaUrl(src.url_s3_url || src.urlS3Url)
    || (url.includes('amazonaws.com') ? url : '');
  if (!url && urlS3) url = urlS3;
  const titleStyle = src.titleStyle
    ? normalizeVideoSubtitleStyle(src.titleStyle, aspectRatio)
    : bookendDefaultTitleStyle(aspectRatio);
  return {
    enabled: Boolean(src.enabled),
    mediaType: src.mediaType === 'video'
      ? 'video'
      : src.mediaType === 'audio'
        ? 'audio'
        : 'image',
    url,
    ...(urlS3 ? { url_s3_url: urlS3 } : {}),
    title: src.title != null ? String(src.title).trim() : String(fallbackTitle || '').trim(),
    durationSeconds: Math.max(1, Math.min(30, Number(src.durationSeconds) || DEFAULT_VIDEO_BOOKEND_SLOT.durationSeconds)),
    titleStyle,
  };
}

export function bookendSlotHasMedia(slot) {
  if (!slot || typeof slot !== 'object') return false;
  return Boolean(bookendSlotMediaUrl(slot));
}

/** Resolved playback/export URL for a bookend slot (prefers S3 when primary is missing). */
export function bookendSlotMediaUrl(slot) {
  if (!slot || typeof slot !== 'object') return '';
  const s3 = String(slot.url_s3_url || slot.urlS3Url || '').trim();
  const raw = String(slot.url || '').trim();
  const resolved = mediaUrlForPlayback(raw || s3, { s3Url: s3 || undefined });
  return String(resolved || raw || s3).trim();
}

export function videoEditorSnapshotHasBookends(savedEditor) {
  if (!savedEditor?.videoBookends || typeof savedEditor.videoBookends !== 'object') return false;
  const b = savedEditor.videoBookends;
  return bookendSlotHasMedia(b.intro) || bookendSlotHasMedia(b.outro);
}

/** Prefer API/cloud bookend URLs (used when refreshing from server on export open). */
export function mergeVideoBookendsFromApi(local, api, { defaultTitle = '', aspectRatio = '16:9' } = {}) {
  const normApi = normalizeVideoBookends(api, { defaultTitle, aspectRatio });
  const normLocal = normalizeVideoBookends(local, { defaultTitle, aspectRatio });
  const mergeSlot = (key) => {
    const a = normApi[key];
    const l = normLocal[key];
    const apiUrl = String(a.url || '').trim();
    const localUrl = String(l.url || '').trim();
    const url = apiUrl || localUrl;
    const urlS3 = String(a.url_s3_url || l.url_s3_url || '').trim()
      || (url.includes('amazonaws.com') ? url : '');
    const apiHasMedia = bookendSlotHasMedia(a);
    return {
      ...l,
      ...a,
      url,
      ...(urlS3 ? { url_s3_url: urlS3 } : {}),
      enabled: apiHasMedia ? a.enabled : l.enabled,
      titleStyle: a.titleStyle?.layer ? a.titleStyle : l.titleStyle,
    };
  };
  return {
    intro: mergeSlot('intro'),
    outro: mergeSlot('outro'),
  };
}

/** Keep uploaded bookend media when cloud snapshot reload is stale or missing URLs. */
export function mergeVideoBookendsPreservingLocal(local, cloud, { defaultTitle = '', aspectRatio = '16:9' } = {}) {
  const normLocal = normalizeVideoBookends(local, { defaultTitle, aspectRatio });
  const normCloud = normalizeVideoBookends(cloud, { defaultTitle, aspectRatio });
  const mergeSlot = (key) => {
    const l = normLocal[key];
    const c = normCloud[key];
    const localUrl = String(l.url || '').trim();
    const cloudUrl = String(c.url || '').trim();
    const url = localUrl || cloudUrl;
    const urlS3 = String(l.url_s3_url || c.url_s3_url || '').trim()
      || (url.includes('amazonaws.com') ? url : '');
    return {
      ...c,
      ...l,
      url,
      ...(urlS3 ? { url_s3_url: urlS3 } : {}),
      enabled: typeof l.enabled === 'boolean' ? l.enabled : c.enabled,
      titleStyle: l.titleStyle?.layer ? l.titleStyle : c.titleStyle,
    };
  };
  return {
    intro: mergeSlot('intro'),
    outro: mergeSlot('outro'),
  };
}

export function persistVideoBookendsForStorage(bookends, { defaultTitle = '', aspectRatio = '16:9' } = {}) {
  const norm = normalizeVideoBookends(bookends, { defaultTitle, aspectRatio });
  const persistSlot = (slot) => {
    const url = persistBookendMediaUrl(slot.url);
    const urlS3 = persistBookendMediaUrl(slot.url_s3_url)
      || (url.includes('amazonaws.com') ? url : '');
    return {
      ...slot,
      url,
      ...(urlS3 ? { url_s3_url: urlS3 } : {}),
    };
  };
  return {
    intro: persistSlot(norm.intro),
    outro: persistSlot(norm.outro),
  };
}

export function normalizeVideoBookends(raw, { defaultTitle = '', aspectRatio = '16:9' } = {}) {
  const base = raw && typeof raw === 'object' ? raw : {};
  const titleDefault = String(defaultTitle || '').trim();
  return {
    intro: normalizeSlot(base.intro, titleDefault, aspectRatio),
    outro: normalizeSlot(base.outro, titleDefault, aspectRatio),
  };
}

function clipEndTime(clip) {
  const start = Number(clip.start_time) || 0;
  const dur = Number(clip.playback_seconds) || Number(clip.duration_seconds) || 0;
  return start + Math.max(0.01, dur);
}

function bookendClipExportPayload(bookend, startTime) {
  if (!bookend?.enabled || !bookendSlotHasMedia(bookend)) return null;
  const url = bookendSlotMediaUrl(bookend);
  if (!url || url.startsWith('blob:')) return null;

  const dur = Math.max(1, Math.min(30, Number(bookend.durationSeconds) || 4));
  const isVideo = bookend.mediaType === 'video';
  return {
    video_url: isVideo ? url : null,
    image_url: isVideo ? null : url,
    trim_start: 0,
    trim_end: dur,
    duration_seconds: dur,
    playback_seconds: dur,
    timeline_seconds: dur,
    video_playback_speed: 1,
    start_time: startTime,
    track_index: 0,
    transition_out: 'cut',
    transition_duration: 0,
    video_filters: [],
    motion_preset: 'none',
    motion_strength: 0.12,
  };
}

function bookendTitleSubtitleCue(title, startTime, duration, titleStyle, aspectRatio) {
  const styleNow = normalizeVideoSubtitleStyle(titleStyle, aspectRatio);
  const layerNow = styleNow.layer || subtitleLayerFromLegacyAnchors(
    styleNow.anchorX,
    styleNow.anchorY,
    aspectRatio,
  );
  const { width: exportFrameW, height: exportFrameH } = exportTargetSizeForAspect(aspectRatio);
  const exportViewportMin = Math.min(exportFrameW, exportFrameH);
  const layout = computeSubtitleLayerTextLayout({
    text: title,
    layer: layerNow,
    style: styleNow,
    frameW: exportFrameW,
    frameH: exportFrameH,
    aspectRatio,
    viewportMin: exportViewportMin,
  });
  const exportAnchors = subtitleLayerToExportAnchors(layerNow);
  const stylePayload = subtitleStyleToExportPayload(styleNow, layerNow, exportAnchors);
  return {
    text: title,
    lines: layout.lines,
    start_time: startTime,
    playback_seconds: duration,
    muted: false,
    layer: layerNow,
    style: stylePayload,
    bookend_title: true,
  };
}

/**
 * Prepend intro and append outro clips; shift main timeline and add title subtitle cues.
 */
export function applyBookendsToExportPayload({
  clips = [],
  subtitleClips = [],
  bookends,
  defaultTitle = '',
  aspectRatio = '16:9',
}) {
  const norm = normalizeVideoBookends(bookends, { defaultTitle, aspectRatio });
  let nextClips = [...clips];
  let nextSubs = [...subtitleClips];
  let hasBookendTitles = false;
  let introTitleStyle = null;

  const introClip = bookendClipExportPayload(norm.intro, 0);
  let introDuration = 0;
  if (introClip) {
    introDuration = introClip.playback_seconds;
    nextClips = nextClips.map((c) => ({
      ...c,
      start_time: (Number(c.start_time) || 0) + introDuration,
    }));
    nextClips = [introClip, ...nextClips];
    const title = norm.intro.title.trim();
    if (title) {
      hasBookendTitles = true;
      introTitleStyle = norm.intro.titleStyle;
      nextSubs.push(bookendTitleSubtitleCue(
        title,
        0,
        introDuration,
        norm.intro.titleStyle,
        aspectRatio,
      ));
    }
    if (introDuration > 0) {
      nextSubs = nextSubs.map((sub) => {
        if (sub.bookend_title) return sub;
        return {
          ...sub,
          start_time: (Number(sub.start_time) || 0) + introDuration,
        };
      });
    }
  }

  const outroClip = bookendClipExportPayload(norm.outro, 0);
  if (outroClip) {
    const mainEnd = nextClips.reduce((mx, c) => Math.max(mx, clipEndTime(c)), 0);
    outroClip.start_time = mainEnd;
    nextClips.push(outroClip);
    const title = norm.outro.title.trim();
    if (title) {
      hasBookendTitles = true;
      nextSubs.push(bookendTitleSubtitleCue(
        title,
        mainEnd,
        outroClip.playback_seconds,
        norm.outro.titleStyle,
        aspectRatio,
      ));
    }
  }

  return {
    clips: nextClips,
    subtitleClips: nextSubs,
    hasBookends: Boolean(introClip || outroClip),
    hasBookendTitles,
    introDuration,
    introTitleStyle,
  };
}

/** @deprecated Use slot.titleStyle — kept for export fallback when only bookend titles exist. */
export function bookendTitleSubtitleStyle(baseStyle, aspectRatio = '16:9') {
  return bookendDefaultTitleStyle(aspectRatio);
}

export function patchBookendTitleLayer(slot, layer, aspectRatio = '16:9') {
  const nextLayer = normalizeSubtitleLayer(layer, aspectRatio);
  return {
    ...slot,
    titleStyle: {
      ...slot.titleStyle,
      layer: nextLayer,
      anchorX: nextLayer.x,
      anchorY: nextLayer.y + nextLayer.height / 2,
    },
  };
}

/** Timeline styling — intro (violet) vs outro (teal). */
export const BOOKEND_TIMELINE_STYLES = {
  intro: {
    bg: 'rgba(139, 92, 246, 0.34)',
    border: '#8b5cf6',
    borderWidth: 2,
    label: 'Intro',
    text: '#ddd6fe',
  },
  outro: {
    bg: 'rgba(20, 184, 166, 0.34)',
    border: '#14b8a6',
    borderWidth: 2,
    label: 'Outro',
    text: '#99f6e4',
  },
};

export function activeBookendDuration(slot) {
  if (!slot?.enabled || !bookendSlotHasMedia(slot)) return 0;
  return Math.max(1, Math.min(30, Number(slot.durationSeconds) || DEFAULT_VIDEO_BOOKEND_SLOT.durationSeconds));
}

export function isBookendTimelineClip(clip) {
  return Boolean(clip?.isBookend);
}

export function bookendClipId(role, lane = 'video') {
  return `bookend-${lane}-${role}`;
}

export function bookendTimelineLayout(bookends, mainContentEnd = 0) {
  const norm = normalizeVideoBookends(bookends);
  const introDur = activeBookendDuration(norm.intro);
  const outroDur = activeBookendDuration(norm.outro);
  const mainEnd = Math.max(0, Number(mainContentEnd) || 0);
  return {
    intro: norm.intro,
    outro: norm.outro,
    introDur,
    outroDur,
    mainEnd,
    totalDuration: introDur + mainEnd + outroDur,
  };
}

export function buildBookendVisualTimelineClip(slot, role, startTime) {
  if (!slot?.enabled || !bookendSlotHasMedia(slot) || slot.mediaType === 'audio') return null;
  const dur = activeBookendDuration(slot);
  const isVideo = slot.mediaType === 'video';
  return {
    id: bookendClipId(role, isVideo ? 'video' : 'image'),
    isBookend: true,
    bookendRole: role,
    name: `${role === 'intro' ? 'Intro' : 'Outro'} bookend`,
    startTime,
    duration: dur,
    playbackDuration: dur,
    trimStart: 0,
    trimEnd: dur,
    ...(isVideo ? { videoUrl: bookendSlotMediaUrl(slot) } : { imageUrl: bookendSlotMediaUrl(slot), videoUrl: null }),
    volume: 1,
    muted: false,
  };
}

export function buildBookendAudioTimelineClip(slot, role, startTime) {
  if (!slot?.enabled || !bookendSlotHasMedia(slot) || slot.mediaType !== 'audio') return null;
  const dur = activeBookendDuration(slot);
  return {
    id: bookendClipId(role, 'audio'),
    isBookend: true,
    bookendRole: role,
    name: `${role === 'intro' ? 'Intro' : 'Outro'} audio`,
    url: bookendSlotMediaUrl(slot),
    startTime,
    duration: dur,
    sourceDuration: dur,
    volume: 0.9,
    muted: false,
  };
}

/** Virtual intro/outro clips + durations for timeline display and playback. */
export function bookendClipsForMainEnd(bookends, mainContentEnd = 0) {
  const layout = bookendTimelineLayout(bookends, mainContentEnd);
  const clips = { video: [], image: [], audio: [] };

  const introVisual = buildBookendVisualTimelineClip(layout.intro, 'intro', 0);
  if (introVisual) {
    if (layout.intro.mediaType === 'video') clips.video.push(introVisual);
    else clips.image.push(introVisual);
  }
  const introAudio = buildBookendAudioTimelineClip(layout.intro, 'intro', 0);
  if (introAudio) clips.audio.push(introAudio);

  const outroStart = layout.introDur + layout.mainEnd;
  const outroVisual = buildBookendVisualTimelineClip(layout.outro, 'outro', outroStart);
  if (outroVisual) {
    if (layout.outro.mediaType === 'video') clips.video.push(outroVisual);
    else clips.image.push(outroVisual);
  }
  const outroAudio = buildBookendAudioTimelineClip(layout.outro, 'outro', outroStart);
  if (outroAudio) clips.audio.push(outroAudio);

  return { ...layout, clips };
}

/**
 * Append compulsory Twinnee brand outro after user bookends (play preview only).
 * Export still concatenates the brand outro server-side after the full timeline.
 *
 * @param {object} timeline — result of bookendClipsForMainEnd
 * @param {{ url?: string, durationSeconds?: number }|null} brandOutro
 */
export function withBrandOutroOnTimeline(timeline, brandOutro) {
  const base = timeline && typeof timeline === 'object'
    ? timeline
    : { clips: { video: [], image: [], audio: [] }, totalDuration: 0, introDur: 0, outroDur: 0, mainEnd: 0 };
  const url = String(brandOutro?.url || '').trim();
  if (!url) {
    return { ...base, brandOutroDur: 0 };
  }
  const dur = Math.max(
    0.5,
    Math.min(60, Number(brandOutro.durationSeconds) || 10),
  );
  const start = Math.max(0, Number(base.totalDuration) || 0);
  const clip = {
    id: 'brand-outro-video',
    isBookend: true,
    bookendRole: 'outro',
    isBrandOutro: true,
    name: 'Twinnee outro',
    startTime: start,
    duration: dur,
    playbackDuration: dur,
    trimStart: 0,
    trimEnd: dur,
    videoUrl: url,
    imageUrl: null,
    volume: 1,
    muted: false,
  };
  const clips = base.clips || { video: [], image: [], audio: [] };
  return {
    ...base,
    brandOutroDur: dur,
    totalDuration: start + dur,
    clips: {
      video: [...(clips.video || []), clip],
      image: [...(clips.image || [])],
      audio: [...(clips.audio || [])],
    },
  };
}

export function offsetTimelineClipForBookendIntro(clip, introDur) {
  if (!clip || isBookendTimelineClip(clip)) return clip;
  const offset = Math.max(0, Number(introDur) || 0);
  if (offset <= 0) return clip;
  return { ...clip, startTime: (Number(clip.startTime) || 0) + offset };
}

export function mergeLaneClipsWithBookends(laneClips, bookendClips, introDur) {
  const main = (laneClips || [])
    .filter((c) => !isBookendTimelineClip(c))
    .map((c) => offsetTimelineClipForBookendIntro(c, introDur));
  return [...(bookendClips || []), ...main].sort(
    (a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0),
  );
}

/** Background music spans full timeline including bookends (starts at 0). */
export function stretchBackgroundMusicForBookends(musicTracks, totalDuration) {
  const dur = Math.max(0.01, Number(totalDuration) || 0);
  return (musicTracks || []).map((track) =>
    (track || []).map((clip) => {
      if (clip?.id !== 'story-background-music' && clip?.source !== 'background_music') return clip;
      return {
        ...clip,
        startTime: 0,
        duration: dur,
        sourceDuration: Math.max(Number(clip.sourceDuration) || dur, dur),
      };
    }),
  );
}

export function bookendAudioExportClips(bookends, mainContentEnd = 0) {
  const layout = bookendTimelineLayout(bookends, mainContentEnd);
  const out = [];
  const push = (slot, role, startTime) => {
    if (!slot?.enabled || slot.mediaType !== 'audio' || !bookendSlotHasMedia(slot)) return;
    const dur = activeBookendDuration(slot);
    const url = bookendSlotMediaUrl(slot);
    if (!url || url.startsWith('blob:')) return;
    out.push({
      audio_url: url,
      start_time: startTime,
      playback_seconds: dur,
      trim_start: 0,
      audio_playback_speed: 1,
      volume: 0.9,
      muted: false,
    });
  };
  push(layout.intro, 'intro', 0);
  push(layout.outro, 'outro', layout.introDur + layout.mainEnd);
  return out;
}

export function bookendTimelineClipStyle(clip, isSelected = false) {
  const role = clip?.bookendRole === 'outro' ? 'outro' : 'intro';
  const pal = BOOKEND_TIMELINE_STYLES[role];
  return {
    background: pal.bg,
    border: `${pal.borderWidth}px solid ${pal.border}`,
    boxShadow: isSelected
      ? `0 0 0 2px white, 0 0 0 4px ${pal.border}`
      : `inset 0 0 0 1px ${pal.border}55`,
    color: pal.text,
  };
}
